<template>
  <div>
    <h1 class="common__main-title">Course status</h1>
    <div class="common__sub-title">
      <span>Details</span>
    </div>
    <div class="common__main-contener">
      <div class="common__main-content bg-white mb-4 p-5" v-if="courseGrade">
        <div>
          <div class="d-flex justify-content-between">
            <h5 class="font-weight-bold">{{ courseGrade.course | courseName }}</h5>
            <div>
              <div class="font-weight-bold">Raw score</div>
              <h4 class="mb-4 text-center">{{ courseGrade.rawScore | rawScore }}</h4>
            </div>
          </div>
          <b-row class="mb-5">
            <b-col cols="6" v-for="(group, idx) of courseGrade.groups" :key="idx">
              <h6 class="mb-3 font-weight-bold">
                lesson {{ group.dayOfWeek | weekday }}. {{ group.period }}
              </h6>
              <div class="mb-2 d-flex">
                <div class="font-weight-bold">Tutor :</div>
                <div class="ml-2">{{ group.tutor && group.tutor.name }}</div>
              </div>
              <div class="mb-2 d-flex">
                <div class="font-weight-bold">Booth :</div>
                <div class="ml-2">{{ group.booth | booth }}</div>
              </div>
              <div class="mb-2 d-flex">
                <div class="mr-2 font-weight-bold">Member:</div>
                <div>
                  <div v-for="student of group.students" :key="student.id">
                    {{ student.name }}
                  </div>
                </div>
              </div>
            </b-col>
          </b-row>
          <div class="mb-5" v-for="sc of courseGrade.specialConsiderations" :key="sc.index">
            <b-row class="mb-3">
              <b-col>
                <b-row>
                  <b-col>
                    <h6 class="mb-3">Special consideration</h6>
                    <div class="mb-2">
                      {{ sc.specialConsideration && sc.specialConsideration.name }}
                    </div>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col>
                    <h6 class="mb-3">Comment</h6>
                    <div class="mb-2">
                      {{ sc.comment }}
                    </div>
                  </b-col>
                </b-row>
              </b-col>
            </b-row>
          </div>
          <b-row class="mb-5" v-for="grade of courseGrade.lessonStudentGrades" :key="grade.index">
            <b-col>
              <div class="w-100 d-flex unit-title">
                <div class="mr-3">
                  Unit {{ grade.unit }} {{ grade.date.format('M/D') }} {{ grade.period }}
                </div>
                <div class="">{{ grade.tutor && grade.tutor.name }}</div>
              </div>
              <b-row class="w-75 mb-3 d-flex justify-content-between">
                <b-col class="font-weight-bold ml-3">Preparation:</b-col>
                <b-col>{{grade.preparation}}</b-col>
              </b-row>
              <b-row class="w-75 mb-3 d-flex justify-content-between"
                v-for="point of grade.points" :key="point.index">
                <b-col class="font-weight-bold ml-3">{{ point.name }}:</b-col>
                <b-col>
                  <template>
                    <b-row>
                      <b-col v-if="point.beforeDescription !== null">
                        {{ point.beforeDescription }}
                      </b-col>
                      <b-col v-if="point.beforeDescription !== null">→</b-col>
                      <b-col>{{ point.description }}</b-col>
                    </b-row>
                  </template>
                </b-col>
              </b-row>
              <div class="w-75 mb-3">
                <div class="mb-2 ml-3 font-weight-bold">Comment:</div>
                <div v-if="grade.beforeComment !== null" class="ml-4">
                  <div style="white-space:pre-wrap;">
                    {{ grade.beforeComment }}
                  </div>
                  <h5 class="text-center my-2">↓</h5>
                </div>
                <div class="ml-4" style="white-space:pre-wrap;">
                  {{ grade.comment }}
                </div>
              </div>
            </b-col>
          </b-row>

          <!-- FRS -->
          <b-row class="mb-5" v-for="(frs, index) of courseGrade.finalReviewSheets" :key="index">
            <b-col>
              <div class="w-50 d-flex justify-content-between">
                <h6 class="mb-3">FRS {{ index + 1 }}</h6>
                <h6 class="mb-3">{{ frs.tutor && frs.tutor.name }}</h6>
              </div>
              <div class="w-75 mb-3">
                <div>Comment:</div>
                <div v-if="frs.beforeComment !== null"
                  class="ml-4" style="white-space:pre-wrap;"
                >
                  {{ frs.beforeComment }}

                  ↓

                </div>
                <div class="ml-4" style="white-space:pre-wrap;">
                  {{ frs.comment }}
                </div>
              </div>
            </b-col>
          </b-row>
        </div>
      </div>
      <div class="d-flex justify-content-between">
          <b-button
            pill
            variant="secondary"
            :to="{ name:'StudentDetail', params: { id: $route.params.studentId } }"
          >
            Back
          </b-button>
        </div>
    </div>
    <loading :isLoading="isLoading" />
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import { CourseGrade } from '@/store/students/types'
import { weekdayKey, Weekdays } from '@/store/common.types'
import { Booth } from '@/store/booths/types'
import Loading from '@/components/parts/modal/Loading.vue'

export default Vue.extend({
  components: {
    Loading,
  },
  filters: {
    courseName(course: CourseGrade['course']|undefined): string {
      return course?.name || ''
    },
    rawScore(rawScore: string): string {
      return rawScore !== null ? rawScore : '-----'
    },
    weekday: (w: weekdayKey) => Weekdays[w],
    booth: (b: Booth): string => (b && `${b.building}-${b.room}-${b.number}`) || '',
  },

  computed: {
    courseGrade(): CourseGrade|undefined {
      return this.$store.getters.findStudentCourseGrades({
        studentId: this.$route.params.studentId,
        courseHistoryId: this.$route.params.courseHistoryId,
      })
    },
  },
  data: (): {
    isLoading: boolean
  } => ({
    isLoading: false,
  }),

  created() {
    this.isLoading = true
    this.$store.dispatch('getStudentCourseGrade', {
      studentId: this.$route.params.studentId,
      courseHistoryId: this.$route.params.courseHistoryId,
    })
      .finally(() => { this.isLoading = false })
  },
})
</script>

<style>
.unit-title {
  padding:10px;
  margin-bottom: 20px;
  background: #D8D8D8;
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: bold;
}
</style>
